import React from 'react';
import { Eye, Download, Trash2 } from 'lucide-react';
import { formatDisplayDate, formatFullTimestamp, getStatusClass, getStatusLabel, getStatusValue, isExceededTimeline, isDueSoon } from '../utils/proposalHelpers';

const formatValuationNumber = (val) => {
    if (!val && val !== 0) return '';
    const cleanStr = String(val).trim();
    if (!cleanStr || cleanStr === 'Not Specified' || cleanStr === '0') return '';
    const numericStr = cleanStr.replace(/[^0-9.]/g, '');
    if (!numericStr) return cleanStr;
    const parts = numericStr.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ? `.${parts[1].slice(0, 2)}` : '';
    return integerPart + decimalPart;
};

/**
 * ProposalCard — extracted from MyProposals.jsx (Phase 7 refactor).
 * Renders a single proposal card in the manager grid view.
 * Zero logic changes. All handlers passed as props from parent.
 */
const ProposalCard = ({ prop, isSelected, isManager, onToggleSelect, onActionClick }) => {
    const rfpId = prop._id || prop.rfp_id || prop.id;
    const dateValTemp = prop.proposal_deadline && prop.proposal_deadline !== 'Not Specified'
        ? prop.proposal_deadline
        : (prop.extraction_data?.submission_deadline || 'N/A');
    const formattedDate = formatDisplayDate(dateValTemp);
    const isExceeded = isExceededTimeline(dateValTemp);
    const isSoon = isDueSoon(dateValTemp);
    const rawStatus = prop.proposal_stage || getStatusValue(prop);
    const statusCls = getStatusClass(rawStatus);
    const statusLabel = rawStatus ? getStatusLabel(rawStatus) : 'Draft';
    const title = prop.proposal_title || prop.extraction_data?.proposal_title || 'Untitled Proposal';
    const client = prop.client_name || prop.extraction_data?.client_name || 'Unknown Client';
    const s = (rawStatus || '').toLowerCase();
    const isFinalStatus = s.includes('submit') || s.includes('accept') || s.includes('reject');
    const queryDateVal = prop.query_deadline && prop.query_deadline !== 'Not Specified'
        ? prop.query_deadline
        : (prop.extraction_data?.query_deadline || 'N/A');
    const formattedQueryDate = formatDisplayDate(queryDateVal);
    const valValue = prop.valuation || prop.budget || prop.extraction_data?.valuation || '';
    const currency = String(prop.currency || prop.CURRENCY || prop.extraction_data?.currency || 'AED').toUpperCase();
    const formattedVal = formatValuationNumber(prop.valuation_value || valValue || prop.valuation_display);
    const shouldShowUrgency = !isFinalStatus && (isExceeded || isSoon);

    const isWon = String(rawStatus).toLowerCase().includes('won') || String(rawStatus).toLowerCase().includes('accepted') || prop.global_status?.code === 6;
    const isLost = String(rawStatus).toLowerCase().includes('lost') || String(rawStatus).toLowerCase().includes('rejected') || prop.global_status?.code === 7;

    return (
        <div
            className={`proposal-card ${isSelected ? 'selected' : ''} ${shouldShowUrgency ? 'urgent-card' : ''}`}
            onClick={(e) => onActionClick(e, 'view', prop)}
        >
            <div className="proposal-card-thumbnail">
                {isManager && (
                    <div 
                        className={`card-checkbox-wrapper ${isSelected ? 'checked' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleSelect(rfpId);
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="card-checkbox-input"
                        />
                    </div>
                )}
                {(() => {
                    const st = (rawStatus || '').toLowerCase();
                    if (st.includes('submit') || st.includes('accept') || st.includes('reject')) return null;
                    if (isExceeded) return <div className="exceeded-timeline-badge" style={isManager ? { left: '36px', borderTopLeftRadius: '0', borderBottomRightRadius: '8px' } : {}}>EXCEEDED TIMELINE</div>;
                    if (isSoon) return <div className="due-soon-badge" style={isManager ? { left: '36px', borderTopLeftRadius: '0', borderBottomRightRadius: '8px' } : {}}>DUE SOON</div>;
                    return null;
                })()}
                <div className="thumbnail-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#b0b3bb" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                    </svg>
                </div>
                <div className="card-hover-actions">
                    <button className="hover-action-btn view-btn" onClick={(e) => onActionClick(e, 'view', prop)} title="View Details">
                        <Eye size={14} />
                    </button>
                    {isManager && (
                        <button className="hover-action-btn download-btn" onClick={(e) => onActionClick(e, 'export', prop)} title="Download">
                            <Download size={14} />
                        </button>
                    )}
                    <button className="hover-action-btn delete-btn" onClick={(e) => onActionClick(e, 'delete', prop)} title="Delete">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
            <div className="proposal-card-body">
                <div className="proposal-card-title-row">
                    <span className="proposal-card-title" title={title}>{title}</span>
                    <span className={`prop-status-pill ${statusCls}`}>{statusLabel}</span>
                </div>
                {prop.section_title && (
                    <div className="assignment-section-detail">
                        <span className="assignment-badge">Section</span>
                        <strong>{prop.section_title}</strong>
                    </div>
                )}
                <div className="proposal-card-client" title={client}>{client}</div>
                <div className="proposal-card-uploaded">
                    {prop.isAssignment ? 'Assigned: ' : 'Uploaded: '}<span>{formatFullTimestamp(prop.created_at || prop.uploaded_at)}</span>
                </div>
                {formattedVal && (
                    <div className="proposal-card-budget" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>Valuation:</span>
                        <strong>{currency} {formattedVal}</strong>
                    </div>
                )}
                {queryDateVal && queryDateVal !== 'N/A' && queryDateVal !== 'Not Specified' && (
                    <div className="proposal-card-budget" style={{ marginTop: '-2px' }}>
                        Query Due: <strong>{formattedQueryDate}</strong>
                    </div>
                )}
                <div className="proposal-card-footer">
                    <span className="proposal-due-date">
                        {isWon && prop.completed_at ? (
                            <>Completed: <strong>{formatDisplayDate(prop.completed_at)}</strong></>
                        ) : isLost && prop.completed_at ? (
                            <>Rejected: <strong>{formatDisplayDate(prop.completed_at)}</strong></>
                        ) : (
                            <>Due: <strong className={(isExceeded || isSoon) ? 'text-red' : ''}>{formattedDate}</strong></>
                        )}
                    </span>
                    <div className="prop-creator-info">
                        <span className="prop-creator-name">{prop.created_by || 'Meghana V'}</span>
                        {prop.created_by_designation && (
                            <span className="prop-creator-designation">{prop.created_by_designation}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProposalCard;
